import { Animation, ArcRotateCamera, CubicEase, EasingFunction, Scene, Vector3 } from 'babylonjs';
import type { PlaneBasis } from '../solver/types';
import type { RectState } from '../core/types';

const FLY_FRAMES = 39; // ≈ 650ms at 60fps
// Default view-stage orbit angles — a front-on, slightly-elevated 3/4 view
// (α orbits around +Y; +π/2 faces the model's front to match other editors.
// β is the polar angle from +Y; ~75° matches model-viewer's default).
const VIEW_ALPHA = Math.PI / 2;
const VIEW_BETA = Math.PI / 2.4;
const v3 = (t: readonly [number, number, number]) => new Vector3(t[0], t[1], t[2]);

/**
 * Wraps the ArcRotateCamera: orbit/zoom for viewing, an analytic fly-to that
 * faces a detected plane front-on, and a user-move watcher that distinguishes an
 * intentional orbit (→ cancel framing) from an allowed zoom.
 *
 * Fly-to avoids hand-derived alpha/beta sign math by letting Babylon's
 * setPosition() compute the target arc params, then animating to them.
 */
export class CameraRig {
  readonly camera: ArcRotateCamera;
  private framed: { alpha: number; beta: number; target: Vector3 } | null = null;
  private framedAt = 0;
  private defaultPanning = 1000;
  animating = false;

  constructor(scene: Scene, canvas: HTMLCanvasElement) {
    this.camera = new ArcRotateCamera('ejCamera', VIEW_ALPHA, VIEW_BETA, 3, Vector3.Zero(), scene);
    this.camera.attachControl(canvas, true);
    this.camera.wheelDeltaPercentage = 0.02;
    this.camera.pinchDeltaPercentage = 0.02;
    // Zoom toward the point under the cursor (keeps it pinned while zooming), not
    // the orbit target — in BOTH view and framing. During framing this drifts the
    // target, which is fine: panning is off there, so only orbit (alpha/beta) is
    // treated as a framing-cancel (see hasUserMoved).
    this.camera.zoomToMouseLocation = true;
    this.camera.panningSensibility = this.defaultPanning;
    this.camera.minZ = 0.01;
    this.camera.angularSensibilityX = 700;
    this.camera.angularSensibilityY = 700;
    this.camera.inertia = 0.85;
  }

  /** Frame a freshly loaded model: back off so the bounding sphere fits the fov. */
  frameModel(center: Vector3, boundingRadius: number): void {
    // Reset to a clean view-stage control state — a prior framing may have left
    // panning disabled / cursor-zoom off, and we might be replacing a framed model.
    this.camera.panningSensibility = this.defaultPanning;
    this.camera.zoomToMouseLocation = true;
    this.framed = null;
    // setTarget() rebuilds alpha/beta/radius from the camera's (stale) position vs
    // the new target — so set the orbit angles AFTER it, or every load lands at a
    // different, often top-down, angle. This pins a consistent front-on framing.
    this.camera.setTarget(center.clone());
    this.camera.alpha = VIEW_ALPHA;
    this.camera.beta = VIEW_BETA;
    const fit = (boundingRadius / Math.sin(this.camera.fov / 2)) * 1.1;
    this.camera.radius = fit;
    this.camera.lowerRadiusLimit = boundingRadius * 0.2;
    this.camera.upperRadiusLimit = fit * 3;
    this.camera.minZ = Math.max(0.001, boundingRadius * 0.01);
    this.camera.maxZ = fit * 20;
  }

  /** Animate to face the rect's plane front-on, framing it with margin. */
  async flyToPlane(basis: PlaneBasis, rect: RectState): Promise<void> {
    const scene = this.camera.getScene();
    const aspect = scene.getEngine().getRenderWidth() / Math.max(1, scene.getEngine().getRenderHeight());
    const halfFov = this.camera.fov / 2;
    const rv = rect.halfH / Math.tan(halfFov);
    const rh = rect.halfW / (Math.tan(halfFov) * aspect);
    const radius = 1.15 * Math.max(rv, rh, 1e-3);

    const center = v3(basis.origin)
      .add(v3(basis.right).scale(rect.center[0]))
      .add(v3(basis.up).scale(rect.center[1]));
    const pos = center.add(v3(basis.normal).scale(radius));

    // Capture current state.
    const fromAlpha = this.camera.alpha;
    const fromBeta = this.camera.beta;
    const fromRadius = this.camera.radius;
    const fromTarget = this.camera.target.clone();

    // Let Babylon compute the destination arc params, then restore.
    this.camera.setTarget(center);
    this.camera.setPosition(pos);
    const toAlpha = shortestAlpha(fromAlpha, this.camera.alpha);
    const toBeta = Math.max(0.05, Math.min(Math.PI - 0.05, this.camera.beta));
    const toRadius = this.camera.radius;
    this.camera.alpha = fromAlpha;
    this.camera.beta = fromBeta;
    this.camera.radius = fromRadius;
    this.camera.setTarget(fromTarget);

    const ease = new CubicEase();
    ease.setEasingMode(EasingFunction.EASINGMODE_EASEOUT);
    const loop = Animation.ANIMATIONLOOPMODE_CONSTANT;
    this.animating = true;
    Animation.CreateAndStartAnimation('ejFlyA', this.camera, 'alpha', 60, FLY_FRAMES, fromAlpha, toAlpha, loop, ease);
    Animation.CreateAndStartAnimation('ejFlyB', this.camera, 'beta', 60, FLY_FRAMES, fromBeta, toBeta, loop, ease);
    Animation.CreateAndStartAnimation('ejFlyR', this.camera, 'radius', 60, FLY_FRAMES, fromRadius, toRadius, loop, ease);
    // Resolve on the LAST animation's actual end (not a timer) so the framed
    // baseline is captured at the true final pose, not mid-flight.
    await new Promise<void>((resolve) => {
      Animation.CreateAndStartAnimation('ejFlyT', this.camera, 'target', 60, FLY_FRAMES, fromTarget, center, loop, ease, () =>
        resolve(),
      );
      // Safety net if the engine drops the callback.
      delay((FLY_FRAMES / 60) * 1000 + 250).then(resolve);
    });
    this.animating = false;
    this.setFramedBaseline();
  }

  /** Disable panning and remember the framed orientation. Zoom — including
   *  zoom-to-cursor — stays enabled; only orbiting cancels framing. */
  lockForFraming(): void {
    this.camera.panningSensibility = 0;
    this.setFramedBaseline();
  }

  unlock(): void {
    this.camera.panningSensibility = this.defaultPanning;
    this.framed = null;
  }

  private setFramedBaseline(): void {
    this.framed = { alpha: this.camera.alpha, beta: this.camera.beta, target: this.camera.target.clone() };
    this.framedAt = now();
  }

  /** True if the user has orbited beyond threshold since framing. Zoom is allowed
   *  and ignored — neither radius nor target is checked, because zoom-to-cursor
   *  legitimately drifts the target (panning is disabled during framing, so
   *  nothing else moves it). A short grace period absorbs post-fly-to settling. */
  hasUserMoved(): boolean {
    if (!this.framed || this.animating || now() - this.framedAt < 350) return false;
    const dAlpha = Math.abs(this.camera.alpha - this.framed.alpha);
    const dBeta = Math.abs(this.camera.beta - this.framed.beta);
    return dAlpha > 0.02 || dBeta > 0.02;
  }
}

function shortestAlpha(from: number, to: number): number {
  let d = to - from;
  while (d > Math.PI) d -= 2 * Math.PI;
  while (d < -Math.PI) d += 2 * Math.PI;
  return from + d;
}

const delay = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
